import React, { useEffect } from 'react'
import useRefreshTimestamp from 'hooks/useRefreshTimestamp'

import { useAppDispatch } from 'app/hooks'
import { useDashboardData, useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { SocialWebChannelsWidget } from 'schemas/SocialWebChannelsWidgetSchema'
import { Dashboard } from 'schemas/DashboardSchema'
import { getWidgetForDashboard } from 'features/widget/asyncThunks'
import { createInsightCount } from 'utils/runSearchUtil'
import { toFilter } from 'utils/widgetUtil'
import Measure from 'enums/Measure'
import Category from 'enums/Category'
import GroupMetric from 'enums/GroupMetric'
import ChannelComparisonBinaryDisplayOption from 'enums/ChannelComparisonBinaryDisplayOption'

const createSearch = (widgetSetting: SocialWebChannelsWidget, dashboardData: Dashboard) => {
  const filter = toFilter(widgetSetting)
  const measure = Measure.POSTS
  const { precision, selectedTopic, selectedDisplayOptions = [], groupMetrics = [] } = widgetSetting
  const { id: dashboardId, name } = dashboardData
  const shouldAddBrandPostsMetric =
    selectedDisplayOptions.includes(
      ChannelComparisonBinaryDisplayOption.HIDE_WITH_ZERO_BRAND_POSTS
    ) && !groupMetrics.includes(GroupMetric.BRAND_POSTS)

  return createInsightCount(
    dashboardId!,
    name!,
    precision!,
    [selectedTopic.topicId!],
    filter,
    measure,
    [Category.CHANNELS_METRICS],
    {
      groupMetrics: shouldAddBrandPostsMetric
        ? [...groupMetrics, GroupMetric.BRAND_POSTS]
        : groupMetrics
    }
  )
}

interface RunSearchEffectProps {
  widgetId: number
  children: React.ReactElement
}

const RunSearchEffect = ({ widgetId, children }: RunSearchEffectProps): React.ReactElement => {
  const widgetSetting = useDashboardWidgetSetting<SocialWebChannelsWidget>(widgetId)
  const dashboardData = useDashboardData()
  const dispatch = useAppDispatch()
  const refreshTimestamp = useRefreshTimestamp(widgetId)

  useEffect(() => {
    const search = createSearch(widgetSetting, dashboardData!)

    dispatch(
      getWidgetForDashboard({
        searches: [search],
        widgetId: widgetSetting.widgetId!,
        timestamp: refreshTimestamp
      })
    )
  }, [widgetSetting, dashboardData, dispatch, refreshTimestamp])

  return children
}

export default RunSearchEffect
